import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { sendStudentMessage } from '@/services/firebaseService';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, Send } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface ContactStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  studentEmail: string;
  studentName: string;
}

export const ContactStudentModal: React.FC<ContactStudentModalProps> = ({
  isOpen,
  onClose,
  studentId,
  studentEmail,
  studentName,
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      toast({
        title: t('common.error'),
        description: t('contact.form.error'),
        variant: 'destructive',
      });
      return;
    }
    if (!user) {
        toast({
            title: t('common.error'),
            description: "User not authenticated.",
            variant: 'destructive',
        });
        return;
    }

    setIsSubmitting(true);
    try {
      await sendStudentMessage({
        userId: user.userId,
        from: user.email,
        replyTo: user.email,
        studentId,
        studentEmail,
        subject: formData.subject,
        message: formData.message,
      });

      toast({
        title: t('contact.form.thankYou'),
        description: t('contact.form.success'),
      });

      setFormData({ subject: '', message: '' });
      onClose();
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('contact.form.error'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh] flex flex-col" : "sm:max-w-[425px]"}>
        <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-0 bg-gray-50" : ""}>
          <DialogTitle>{t('students.contactStudent')}</DialogTitle>
          <DialogDescription>
            {t('students.sendMessageTo', { studentName: studentName })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className={isMobile ? "flex flex-col flex-1 overflow-hidden" : "space-y-4 py-4"}>
          <div className={cn("space-y-4", isMobile ? "flex-1 overflow-y-auto p-6" : "px-6")}>
            <div className="space-y-2">
              <Label htmlFor="subject">{t('contact.form.subject')}</Label>
              <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">{t('contact.form.message')}</Label>
              <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} />
            </div>
          </div>
          <DialogFooter className={isMobile ? "px-6 py-4 border-t" : ""}>
            <Button type="button" variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {isSubmitting ? t('common.sending') : t('common.send')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};